import { formatForDisplay } from '@packages/core';
import type { FormFieldMapping } from '../types/fieldMapping';
import { formatKeyToDisplayFormat } from './displayFormatKeys';

export type SyncFormatter = (value: string, prevValue: string) => string;

export interface ResolveFieldDisplayValueOptions {
  selectOptions?: readonly { label: string; value: unknown }[];
  /** Active sync formatter (from field.def.formatter or fieldState.formatter). */
  formatter?: SyncFormatter;
  /** endAddon / startAddon suffix (e.g. "DAYS"). */
  addon?: unknown;
  /** maskedFormatKey for unmasked display formatting. */
  maskedFormatKey?: string;
}

export function resolveSyncFormatter(
  formatter: FormFieldMapping['def']['formatter'] | SyncFormatter | undefined,
): SyncFormatter | undefined {
  if (!formatter) return undefined;
  if (typeof formatter === 'function') return formatter;
  if (typeof formatter === 'string') return undefined;
  if (Array.isArray(formatter)) {
    return (value: string, prevValue: string) =>
      formatter.reduce((v, fn, i) => fn(v, i === 0 ? prevValue : v), value);
  }
  return undefined;
}

function resolveSelectLabels(
  value: unknown,
  options: readonly { label: string; value: unknown }[],
): string | undefined {
  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => {
        const match = options.find((option) => String(option.value) === String(entry));
        return match?.label ?? (entry != null && entry !== '' ? String(entry) : null);
      })
      .filter((label): label is string => label != null && label !== '');
    return labels.length > 0 ? labels.join(', ') : undefined;
  }

  if (value == null || value === '') return undefined;
  const match = options.find((option) => String(option.value) === String(value));
  return match?.label;
}

function resolveBooleanLabel(value: unknown): string | undefined {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return undefined;
}

function applySyncFormatter(value: unknown, formatter: SyncFormatter | undefined): unknown {
  if (formatter == null || value == null || value === '') return value;
  if (typeof value !== 'string' && typeof value !== 'number') return value;
  return formatter(String(value), '');
}

/**
 * Single source of truth for read-only field display.
 * Mirrors edit-mode formatting: formatKey/displayFormat → formatForDisplay, plus sync input formatters.
 */
export function resolveFieldDisplayValue(
  value: unknown,
  field: FormFieldMapping,
  options: ResolveFieldDisplayValueOptions = {},
): string {
  const { selectOptions, addon, maskedFormatKey } = options;
  const syncFormatter =
    options.formatter ?? resolveSyncFormatter(field.def.formatter as FormFieldMapping['def']['formatter']);

  if (selectOptions?.length) {
    const label = resolveSelectLabels(value, selectOptions);
    if (label != null) return appendAddon(label, addon);
  }

  if (
    field.def.adapterHint === 'checkbox'
    || field.def.adapterHint === 'toggle'
    || typeof value === 'boolean'
  ) {
    const label = resolveBooleanLabel(value);
    if (label != null) return appendAddon(label, addon);
  }

  let normalized = applySyncFormatter(value, syncFormatter);

  const displayFormat =
    (field.def.displayFormat as string | undefined)
    ?? (maskedFormatKey != null ? formatKeyToDisplayFormat(maskedFormatKey) : undefined);

  const display = formatForDisplay(normalized, {
    ...field.def,
    ...(displayFormat != null && { displayFormat }),
  } as Parameters<typeof formatForDisplay>[1]);

  return appendAddon(display, addon);
}

export function appendAddon(display: string, addon: unknown): string {
  if (addon == null || addon === '' || display === '-' || display === '') return display;
  return `${display} ${String(addon)}`;
}
